import { useAuth } from "@clerk/react";
import { useEffect, useRef, type ReactNode } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { AdvisorDrawer } from "../components/advisor/AdvisorDrawer";
import { AdvisorSessionProvider } from "../components/advisor/AdvisorSessionProvider";
import { AdvisorDrawerPreview } from "../components/advisor/AdvisorDrawerPreview";
import { useOpenAdvisorDrawer } from "../hooks/useOpenAdvisorDrawer";
import { useReadiness } from "../hooks/useReadiness";
import { getBrowserPathname, resolveClerkBypass } from "../lib/dev-preview";
import { cn } from "../lib/utils";
import { useAdvisorDrawerStore, useAdvisorStore } from "../stores/advisor";

/**
 * ConsumerFrame (plan 435 M6 integration pass, task asd-consumer-frame).
 *
 * The ONE layout route wrapping every authed consumer route (`/dashboard`,
 * `/dashboard/*`, the marketplace `DashboardShell` group, `/calculators`,
 * `/help`, `/team`, `/threads*`, `/offers*`, `/market/*`, settings) — see
 * `App.tsx`'s wiring and the drawer design doc §4.1. It renders `<Outlet/>`
 * plus the two things that must outlive page navigation: the ONE shared
 * `AdvisorSessionProvider` and the ONE `AdvisorDrawer`, and owns the
 * end-turn readiness refresh watcher (SILL-255 / ADR-027) that used to live
 * in `DashboardPage` — a turn in the drawer on ANY route and a turn on the
 * full page now both refresh through this single watcher.
 *
 * Mirrors `DashboardShell`'s `PreviewDashboardShellInner` fork: DEV
 * `/preview/*` fixture paths render a Clerk-free preview chrome (a static
 * fixture session + `AdvisorDrawerPreview`, never the real
 * `AdvisorSessionProvider`/`AdvisorDrawer`, both of which call Clerk's
 * `useAuth()` unconditionally and would throw with no `ClerkProvider`
 * mounted — the no-key CI posture every other `/preview/*` fixture already
 * accommodates).
 */
export function ConsumerFrame() {
  return (
    <ConsumerFrameChrome>
      <Outlet />
    </ConsumerFrameChrome>
  );
}

/**
 * The presentational half, exported separately so a one-off preview route
 * that cannot use the nested-`Route`/`Outlet` shape (e.g. `/preview/dashboard`
 * swapping between the real `DashboardPage` and a Clerk-free fixture) can
 * still get the same chrome around explicit `children`.
 */
export function ConsumerFrameChrome({ children }: { children: ReactNode }) {
  // `resolveClerkBypass` (not the bare `isDevPreviewPath` check) so a
  // Clerk-mounted local preview of `/preview/dashboard` (a real key
  // configured) still gets the REAL session/drawer — only the no-key CI
  // posture (or a fixture-only path like `/preview/offers`, which is
  // ALWAYS Clerk-free regardless of a key) gets the preview chrome. Mirrors
  // `DashboardShell`'s own fork predicate exactly.
  const hasClerkKey = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
  if (
    import.meta.env.DEV &&
    resolveClerkBypass(getBrowserPathname(), hasClerkKey)
  ) {
    return <PreviewConsumerFrameChrome>{children}</PreviewConsumerFrameChrome>;
  }
  return <AuthedConsumerFrameChrome>{children}</AuthedConsumerFrameChrome>;
}

/**
 * `?ask=1` is the deep-link/fixture flag (drawer design doc §3): open the
 * drawer once on mount when present. It never reflects back into the URL on
 * ordinary open/close — question 308's "persists across navigation" lives
 * in the store, not the URL — this is a one-shot trigger only.
 *
 * Routes through `useOpenAdvisorDrawer` (review fix, iteration 2, finding
 * 3) so this entry point fires `advisor_drawer_opened{source:"deep_link"}`
 * like every other trigger — it previously called the store's `openDrawer`
 * directly and emitted nothing.
 */
function useAskQueryParamOpensDrawer() {
  const [searchParams] = useSearchParams();
  const askParam = searchParams.get("ask");
  const openAdvisorDrawer = useOpenAdvisorDrawer();
  const consumedRef = useRef(false);
  useEffect(() => {
    if (consumedRef.current) return;
    if (askParam !== "1") return;
    consumedRef.current = true;
    openAdvisorDrawer("deep_link");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askParam]);
}

/**
 * Force-closes the drawer whenever the route is the full-page
 * `?view=advisor` so the two shells are never co-mounted (design doc §4.2).
 * Uses `collapseDrawer` (NOT `closeDrawer`) so a context the drawer was
 * already carrying survives for `AdvisorView` to read — this fires on a
 * DIRECT navigation to `?view=advisor` (header pill, a deep link) while the
 * drawer happened to be open; `AdvisorDrawer`'s own Expand already collapses
 * before navigating, so this is mostly a defensive backstop for that case.
 */
function useForceCollapseOnFullPageAdvisor() {
  const [searchParams] = useSearchParams();
  const isFullPageAdvisor = searchParams.get("view") === "advisor";
  const collapseDrawer = useAdvisorDrawerStore((s) => s.collapseDrawer);
  useEffect(() => {
    if (isFullPageAdvisor) collapseDrawer();
  }, [isFullPageAdvisor, collapseDrawer]);
}

/**
 * Question 309 default: `⌘/Ctrl+K` opens the drawer from anywhere in the
 * frame (`/` collides with Firefox's quick-find). Ignored while a text
 * input/textarea/contenteditable is focused — a command-palette shortcut
 * should never steal a keystroke from something the buyer is typing into,
 * including the drawer's own composer.
 */
function useAdvisorDrawerShortcut() {
  const openAdvisorDrawer = useOpenAdvisorDrawer();
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "k") return;
      if (!event.metaKey && !event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      openAdvisorDrawer("shortcut");
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openAdvisorDrawer]);
}

function usePushLayoutClassName(isFullPageAdvisor: boolean): string {
  const drawerOpen = useAdvisorDrawerStore((s) => s.drawerOpen);
  // Push (not overlay) at >=1280px (Tailwind `xl:`) per the drawer design
  // doc's Q1 default; never while the full page itself IS the drawer's
  // destination (no second right-hand gutter to reserve).
  return drawerOpen && !isFullPageAdvisor ? "xl:pr-[420px]" : "";
}

function AuthedConsumerFrameChrome({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const isFullPageAdvisor = searchParams.get("view") === "advisor";

  useAskQueryParamOpensDrawer();
  useForceCollapseOnFullPageAdvisor();
  useAdvisorDrawerShortcut();
  const pushClassName = usePushLayoutClassName(isFullPageAdvisor);

  // SILL-255 / ADR-027: the end-turn readiness refresh watcher, moved here
  // from `DashboardPage` (drawer design doc §4.1) so a turn anywhere — the
  // drawer on any route, or the full page — refreshes through ONE watcher.
  // `useReadiness` reads the SAME shared module-level store every other
  // mount of this hook does; calling it again here is not a second fetch,
  // it is a second subscriber to the one cache (ADR-027).
  const activeReadiness = useReadiness({
    enabled: isLoaded && !!isSignedIn,
    getToken,
  });
  const refreshReadiness = activeReadiness.refresh;
  const advisorIsStreaming = useAdvisorStore((s) => s.isStreaming);
  const wasAdvisorStreamingRef = useRef(false);
  useEffect(() => {
    if (wasAdvisorStreamingRef.current && !advisorIsStreaming) {
      void refreshReadiness();
    }
    wasAdvisorStreamingRef.current = advisorIsStreaming;
  }, [advisorIsStreaming, refreshReadiness]);

  return (
    <AdvisorSessionProvider>
      <div className={cn(pushClassName)} data-slot="consumer-frame-content">
        {children}
      </div>
      {!isFullPageAdvisor ? <AdvisorDrawer /> : null}
    </AdvisorSessionProvider>
  );
}

function PreviewConsumerFrameChrome({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const isFullPageAdvisor = searchParams.get("view") === "advisor";
  const streamingParam = searchParams.get("streaming") === "1";

  useAskQueryParamOpensDrawer();
  useForceCollapseOnFullPageAdvisor();
  useAdvisorDrawerShortcut();
  const pushClassName = usePushLayoutClassName(isFullPageAdvisor);

  return (
    <>
      <div className={cn(pushClassName)} data-slot="consumer-frame-content">
        {children}
      </div>
      {!isFullPageAdvisor ? (
        <AdvisorDrawerPreview streaming={streamingParam} />
      ) : null}
    </>
  );
}
